/**
 * Patient status badge dropdown in the single-patient eligibility header.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useMemo, useState } from "react";
import { clinicalTrialsService } from "../../../api/clinicalTrialsService";
import { useEligibilityOptions } from "../../../hooks/useEligibilityOptions";
import { plainTextToRichText } from "../../../utils/richText";
import { buildPatientStatusBadgeOptions } from "../../../utils/patientStatusBadges";
import { PatientStatusBadgeCell } from "../../PatientEligibilityMappingView/cells/PatientStatusBadgeCell";
import { useEligibilityDetailContext } from "../EligibilityDetailContext";
import { PatientStatusChangeModal } from "./PatientStatusChangeModal";
import type { BadgeWithIconProps } from "../../../data-model/badgeWithIconProps";

interface PatientStatusDropdownProps {
  mrn: string;
  patientName: string;
  patientStatus: string;
  badgeList: BadgeWithIconProps[];
  onSave: (nextStatus: string, comment: string) => Promise<void>;
}

/** Shared patient-status dropdown with confirmation used by headers and tables. */
export function PatientStatusDropdown({
  mrn,
  patientName,
  patientStatus,
  badgeList,
  onSave,
}: PatientStatusDropdownProps) {
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const handlePatientStatusSelection = useCallback(
    async (patientMrn: string, patientStatusValue: string): Promise<void> => {
      if (patientMrn !== mrn) return;
      setPendingStatus(patientStatusValue);
    },
    [mrn],
  );

  return (
    <>
      <PatientStatusBadgeCell
        key={`${patientStatus}-${pendingStatus ?? "idle"}`}
        mrn={mrn}
        value={patientStatus}
        badgeList={badgeList}
        onChange={handlePatientStatusSelection}
      />
      <PatientStatusChangeModal
        isOpen={pendingStatus !== null}
        patientName={patientName}
        patientMrn={mrn}
        currentStatus={patientStatus}
        nextStatus={pendingStatus ?? ""}
        onClose={() => setPendingStatus(null)}
        onSave={(comment) =>
          pendingStatus ? onSave(pendingStatus, comment) : Promise.resolve()
        }
      />
    </>
  );
}

/**
 * Editable patient workflow status control for the details header strip.
 *
 * @returns Badge dropdown wired to patch + detail refresh
 */
export function PatientStatusHeaderDropdown() {
  const { trialId, mrn, detail, refreshDetail } = useEligibilityDetailContext();
  const { options, refresh: refreshOptions } = useEligibilityOptions(trialId);

  const patientStatus = detail.row.patient_status;
  const badgeList = useMemo(
    () => buildPatientStatusBadgeOptions(options?.patient_status ?? []),
    [options?.patient_status],
  );

  /** Persist the confirmed status and create a note only when one was entered. */
  const handleConfirmedStatusChange = useCallback(
    async (nextStatus: string, comment: string): Promise<void> => {
      await clinicalTrialsService.patchEligibilityState(trialId, mrn, {
        patient_status: nextStatus,
        comment: comment || null,
      });
      toast.success("Patient status changed successfully.");

      if (comment) {
        try {
          await clinicalTrialsService.createNote(mrn, {
            title: `Patient status changed to ${nextStatus}`,
            body: plainTextToRichText(comment),
          });
          toast.success("Note added successfully.");
        } catch {
          toast.error(
            "Patient status was changed, but its note could not be added.",
          );
        }
      }

      refreshDetail?.();
      refreshOptions();
    },
    [mrn, refreshDetail, refreshOptions, trialId],
  );

  if (!patientStatus) {
    return null;
  }

  return (
    <PatientStatusDropdown
      mrn={mrn}
      patientName={detail.row.patient_name || "—"}
      patientStatus={patientStatus}
      badgeList={badgeList}
      onSave={handleConfirmedStatusChange}
    />
  );
}

export default PatientStatusHeaderDropdown;
